// Avance propio: el progreso de cada alumno en el vocabulario se guarda en nuestro Firestore en lugar
// del servidor de Inowu (fase 2 de la salida de Inowu, 4 nov 2026).
//
// Dónde vive (una carpeta por alumno, con el uid de Firebase Auth):
//   vocabularioAvance/{uid}                        { vocabularyUserId, dias: ["2026-11-04", …], actualizado }
//   vocabularioAvance/{uid}/bloques/{n}            { p: { "<id>": { d, v, c, f, l } } }  n = floor(id / 1000)
//        d = dominada (-4…3 o null), v = veces respondida, c = veces correcta,
//        f = primera vez (M/D/YYYY), l = última vez (M/D/YYYY). «id» es el mismo id de palabra de Inowu.
//   vocabularioAvance/{uid}/diasDetalle/{YYYY-MM-DD}  { segundos, palabras, dominadas, idiomas: { french: n, … } }
//   vocabularioAvance/{uid}/rondas/{auto}          { fecha, seminario, ids: [..], niveles: { "<id>": n } }
//
// El historial de Inowu se copió el 4 nov 2026 (respaldo-vocabulario/2026-11-04) y se carga con
// scripts/vocabulario/cargar-avance.js. Lo de «diasDetalle» y «rondas» empieza vacío: Inowu no lo daba.
//
// Interruptor (apagado por omisión, como el de las palabras):
//   · REACT_APP_AVANCE=propio lo enciende para todos;
//   · localStorage "avance" = "propio" o "inowu" lo cambia solo en ese navegador, para probar
//     (abrir cualquier página con ?avance=propio o ?avance=inowu lo guarda).
// Encender el avance propio enciende también las palabras propias (no tiene sentido mezclar).

import { collection, doc, getDoc, getDocs, limit, query, setDoc, where, Timestamp, addDoc, increment } from "firebase/firestore";
import { auth, db } from "initFirebase";

export interface Progreso {
	d: number | null;
	v: number;
	c: number;
	f: string | null;
	l: string | null;
}

const IDIOMAS = ["french", "german", "japanese", "italian", "russian", "chinese", "portuguese", "english"];
const TAM_BLOQUE = 1000;

export function usarAvancePropio(): boolean {
	try {
		// Para probar sin saber de programación: abrir el sitio con ?avance=propio (o ?avance=inowu para
		// regresar) lo deja guardado en ese navegador.
		const pedido = new URLSearchParams(window.location.search).get("avance");
		if (pedido === "propio" || pedido === "inowu") window.localStorage.setItem("avance", pedido);
		const local = window.localStorage.getItem("avance");
		if (local === "propio") return true;
		if (local === "inowu") return false;
	} catch {
		// Sin acceso al almacenamiento: manda la configuración del sitio.
	}
	return process.env.REACT_APP_AVANCE === "propio";
}

/**
 * Espejo en Inowu: mientras Inowu siga prendido, lo que el alumno estudia con el avance propio TAMBIÉN se
 * le manda a Inowu (sin esperar respuesta y sin fallar si no contesta). Así, si hay que volver a Inowu
 * durante la prueba, no se pierde nada. Se apaga con REACT_APP_ESPEJO_INOWU=no o localStorage espejo=no,
 * y no aplica a alumnos sin número de Inowu.
 */
export function espejoInowu(vocabularyUserId?: number | string): boolean {
	if (!process.env.REACT_APP_API_URL || process.env.REACT_APP_ESPEJO_INOWU === "no") return false;
	try {
		if (window.localStorage.getItem("espejo") === "no") return false;
	} catch {
		// sin almacenamiento: sigue la configuración
	}
	return vocabularyUserId === undefined || /^\d+$/.test(String(vocabularyUserId));
}

/** Corre la llamada a Inowu en segundo plano; nunca lanza error. */
export function enEspejo(llamada: () => Promise<any>) {
	try {
		llamada().catch((error) => console.warn("Espejo en Inowu falló (no afecta al alumno):", error?.message || error));
	} catch (error) {
		console.warn("Espejo en Inowu falló (no afecta al alumno):", error);
	}
}

// ---------------------------------------------------------------------------------------------
// ¿De quién es? El sitio pasa el «vocabularyUserId» de Inowu (un número). En la carpeta de cada alumno
// guardamos ese número para encontrarla. A los alumnos nuevos (sin número de Inowu) se les da su uid
// como vocabularyUserId, así que un valor que no es número ya es el uid.
// ---------------------------------------------------------------------------------------------

const uidPorNumero = new Map<string, Promise<string>>();

export function uidDeAlumno(vocabularyUserId: number | string): Promise<string> {
	const clave = String(vocabularyUserId);
	if (!/^\d+$/.test(clave)) return Promise.resolve(clave);
	if (!uidPorNumero.has(clave)) {
		const busqueda = (async () => {
			// Casi siempre es el alumno que está usando el sitio: su carpeta es su uid.
			const yo = auth.currentUser;
			if (yo) {
				const perfil = await getDoc(doc(db, "users", yo.uid));
				if (String(perfil.data()?.vocabularyUserId) === clave) return yo.uid;
			}
			// Otro alumno (lo ve un administrador): se busca por su número.
			const snap = await getDocs(
				query(collection(db, "vocabularioAvance"), where("vocabularyUserId", "==", Number(clave)), limit(1))
			);
			if (!snap.empty) return snap.docs[0].id;
			throw new Error(`No hay avance propio para el alumno ${clave}`);
		})();
		busqueda.catch(() => uidPorNumero.delete(clave));
		uidPorNumero.set(clave, busqueda);
	}
	return uidPorNumero.get(clave)!;
}

// ---------------------------------------------------------------------------------------------
// Lectura del avance completo (todos los bloques de un alumno; con ~25 mil palabras son ~40 lecturas).
// ---------------------------------------------------------------------------------------------

const avancePorUid = new Map<string, Promise<Map<number, Progreso>>>();

export function avanceDe(uid: string): Promise<Map<number, Progreso>> {
	if (!avancePorUid.has(uid)) {
		const pedido = getDocs(collection(db, "vocabularioAvance", uid, "bloques")).then((snap) => {
			const mapa = new Map<number, Progreso>();
			snap.forEach((bloque) => {
				const p = (bloque.data().p || {}) as Record<string, Progreso>;
				Object.entries(p).forEach(([id, valor]) => mapa.set(Number(id), valor));
			});
			return mapa;
		});
		pedido.catch(() => avancePorUid.delete(uid));
		avancePorUid.set(uid, pedido);
	}
	return avancePorUid.get(uid)!;
}

export async function avanceDeAlumno(vocabularyUserId: number | string) {
	return avanceDe(await uidDeAlumno(vocabularyUserId));
}

/** Los campos de progreso con la misma forma que los daba Inowu en `seminary/{alumno}/{n}`. */
export function camposComoInowu(p: Progreso | undefined) {
	if (!p) {
		return {
			dominada: null,
			veces_respondida: null,
			veces_respondida_correcta: null,
			dificultadP: "0",
			firstStudied: null,
			lastStudied: null,
		};
	}
	return {
		dominada: p.d,
		veces_respondida: String(p.v),
		veces_respondida_correcta: String(p.c),
		dificultadP: (p.v > 0 ? p.c / p.v : 0).toFixed(4),
		firstStudied: p.f,
		lastStudied: p.l,
	};
}

// ---------------------------------------------------------------------------------------------
// Escritura: al terminar una ronda (lo que antes era POST user/progress).
// Las veces respondida/correcta que manda la ronda son de esa ronda y se suman; la dominada se reemplaza.
// ---------------------------------------------------------------------------------------------

export async function guardarProgreso(
	vocabularyUserId: number | string,
	palabras: Array<Record<string, any>>,
	fecha: string,
	segundos: number
) {
	const uid = await uidDeAlumno(vocabularyUserId);
	const mapa = await avanceDe(uid);
	const porBloque = new Map<number, Record<string, Progreso>>();
	const idiomas: Record<string, number> = {};
	let dominadasNuevas = 0;

	for (const palabra of palabras) {
		const id = Number(palabra.id);
		if (!Number.isFinite(id)) continue;
		const antes = mapa.get(id);
		const despues: Progreso = {
			d: palabra.dominated ?? antes?.d ?? null,
			v: (antes?.v ?? 0) + Number(palabra.times_answered ?? 0),
			c: (antes?.c ?? 0) + Number(palabra.times_correct_answer ?? 0),
			f: antes?.f ?? fecha,
			l: fecha,
		};
		if (despues.d === 3 && antes?.d !== 3) dominadasNuevas++;
		mapa.set(id, despues);
		const n = Math.floor(id / TAM_BLOQUE);
		if (!porBloque.has(n)) porBloque.set(n, {});
		porBloque.get(n)![String(id)] = despues;
		if (palabra.language) idiomas[palabra.language] = (idiomas[palabra.language] ?? 0) + 1;
	}

	const raiz = doc(db, "vocabularioAvance", uid);
	await setDoc(
		raiz,
		{ actualizado: Timestamp.now(), ...(/^\d+$/.test(String(vocabularyUserId)) ? { vocabularyUserId: Number(vocabularyUserId) } : {}) },
		{ merge: true }
	);
	await Promise.all(
		Array.from(porBloque.entries()).map(([n, p]) =>
			setDoc(doc(db, "vocabularioAvance", uid, "bloques", String(n)), { p }, { merge: true })
		)
	);

	// Resumen del día (lo que muestra el calendario de la racha).
	const dia = diaLocal(new Date());
	await setDoc(
		doc(db, "vocabularioAvance", uid, "diasDetalle", dia),
		{
			segundos: increment(Number(segundos) || 0),
			palabras: increment(palabras.length),
			dominadas: increment(dominadasNuevas),
			idiomas: Object.fromEntries(Object.entries(idiomas).map(([k, n]) => [k, increment(n)])),
		},
		{ merge: true }
	);
	return { data: { message: "ok" } };
}

/** Igual que `words/dominatedWordsByArray/{alumno}`: las palabras con su «dominated» actual. */
export async function palabrasConDominada(vocabularyUserId: number | string, palabras: Array<Record<string, any>>) {
	const mapa = await avanceDeAlumno(vocabularyUserId);
	return {
		data: {
			words: palabras.map((p) => ({ ...p, dominated: mapa.get(Number(p.id))?.d ?? null })),
		},
	};
}

// ---------------------------------------------------------------------------------------------
// Racha y días estudiados (antes user/{id}/streak, study-day y migrate-firestore).
// Los días se guardan como fecha local «YYYY-MM-DD» y se devuelven como «YYYY-MM-DDT00:00:00.000Z», igual que Inowu.
// ---------------------------------------------------------------------------------------------

function diaLocal(fecha: Date, zona?: string): string {
	const partes = new Intl.DateTimeFormat("en-CA", {
		timeZone: zona || Intl.DateTimeFormat().resolvedOptions().timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(fecha);
	return partes; // en-CA ya da YYYY-MM-DD
}

function sumarDias(dia: string, n: number): string {
	const d = new Date(dia + "T12:00:00Z");
	d.setUTCDate(d.getUTCDate() + n);
	return d.toISOString().slice(0, 10);
}

export function calcularRacha(dias: string[], hoy: string) {
	const unicos = Array.from(new Set(dias)).sort();
	let maxStreak = 0;
	let corrida = 0;
	let anterior = "";
	for (const d of unicos) {
		corrida = anterior && sumarDias(anterior, 1) === d ? corrida + 1 : 1;
		maxStreak = Math.max(maxStreak, corrida);
		anterior = d;
	}
	const conjunto = new Set(unicos);
	let currentStreak = 0;
	let cursor = conjunto.has(hoy) ? hoy : sumarDias(hoy, -1);
	while (conjunto.has(cursor)) {
		currentStreak++;
		cursor = sumarDias(cursor, -1);
	}
	return {
		currentStreak,
		maxStreak,
		studiedDays: unicos.map((d) => `${d}T00:00:00.000Z`),
	};
}

async function diasDe(uid: string): Promise<string[]> {
	const snap = await getDoc(doc(db, "vocabularioAvance", uid));
	return (snap.data()?.dias as string[]) || [];
}

export async function rachaPropia(vocabularyUserId: number | string, zona: string) {
	const uid = await uidDeAlumno(vocabularyUserId);
	return calcularRacha(await diasDe(uid), diaLocal(new Date(), zona));
}

export async function agregarDiaPropio(vocabularyUserId: number | string, fechaIso: string, zona: string) {
	const uid = await uidDeAlumno(vocabularyUserId);
	const dias = await diasDe(uid);
	const dia = diaLocal(new Date(fechaIso), zona);
	if (!dias.includes(dia)) {
		await setDoc(doc(db, "vocabularioAvance", uid), { dias: [...dias, dia].sort() }, { merge: true });
	}
	return calcularRacha([...dias, dia], diaLocal(new Date(), zona));
}

/** Días viejos guardados en users/{uid}.studiedDays (formato MM/dd/yyyy). */
export async function migrarDiasPropio(vocabularyUserId: number | string, fechas: string[], zona: string) {
	const uid = await uidDeAlumno(vocabularyUserId);
	const dias = await diasDe(uid);
	const nuevos = fechas
		.map((f) => {
			const m = String(f).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
			return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : null;
		})
		.filter((d): d is string => !!d);
	const todos = Array.from(new Set([...dias, ...nuevos])).sort();
	await setDoc(doc(db, "vocabularioAvance", uid), { dias: todos }, { merge: true });
	return calcularRacha(todos, diaLocal(new Date(), zona));
}

/** Igual que `studied-words/{M-D-YYYY}/{alumno}`. */
export async function datosDelDiaPropio(fechaGuiones: string, vocabularyUserId: number | string) {
	const uid = await uidDeAlumno(vocabularyUserId);
	const m = fechaGuiones.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
	const dia = m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : fechaGuiones;
	const snap = await getDoc(doc(db, "vocabularioAvance", uid, "diasDetalle", dia));
	const datos = snap.data();
	if (!datos) {
		return { dateInfo: { studiedTime: 0, studiedWords: 0, dominatedWords: 0, mostStudiedLang: "Sin registro" } };
	}
	const idiomas = Object.entries((datos.idiomas || {}) as Record<string, number>).sort((a, b) => b[1] - a[1]);
	return {
		dateInfo: {
			studiedTime: datos.segundos || 0,
			studiedWords: datos.palabras || 0,
			dominatedWords: datos.dominadas || 0,
			mostStudiedLang: idiomas.length ? idiomas[0][0] : "Sin registro",
		},
	};
}

// ---------------------------------------------------------------------------------------------
// Historial de rondas (antes POST/GET study-history): qué palabras estudió y cuándo, para el filtro
// «palabras estudiadas en tal periodo» del examen personalizado.
// ---------------------------------------------------------------------------------------------

export async function registrarRondaPropia(
	vocabularyUserId: number | string,
	seminario: number | string,
	palabras: Array<{ word_data_id: number; mastery_level: number }>
) {
	const uid = await uidDeAlumno(vocabularyUserId);
	await addDoc(collection(db, "vocabularioAvance", uid, "rondas"), {
		fecha: Timestamp.now(),
		seminario: Number(seminario),
		ids: palabras.map((p) => p.word_data_id),
		niveles: Object.fromEntries(palabras.map((p) => [String(p.word_data_id), p.mastery_level])),
	});
	return { message: "Words studied save successfully", statusCode: 201 };
}

export async function rondasEnPeriodoPropio(params: {
	userId: number | string;
	timePeriod: string;
	minuteInterval?: number;
	startDate?: string;
	endDate?: string;
	masteryLevel?: number;
}) {
	const uid = auth.currentUser?.uid || (await uidDeAlumno(params.userId));
	let desde: Date;
	let hasta: Date = new Date();
	if (params.timePeriod === "last_minutes" && params.minuteInterval) {
		desde = new Date(Date.now() - params.minuteInterval * 60000);
	} else if (params.startDate) {
		desde = new Date(params.startDate);
		if (params.endDate) hasta = new Date(params.endDate);
	} else {
		desde = new Date(0);
	}
	const snap = await getDocs(
		query(
			collection(db, "vocabularioAvance", uid, "rondas"),
			where("fecha", ">=", Timestamp.fromDate(desde)),
			where("fecha", "<=", Timestamp.fromDate(hasta))
		)
	);
	const ids = new Set<number>();
	snap.forEach((r) => {
		const datos = r.data();
		(datos.ids || []).forEach((id: number) => {
			if (params.masteryLevel === undefined || datos.niveles?.[String(id)] === params.masteryLevel) ids.add(id);
		});
	});
	return {
		data: {
			words: Array.from(ids).map((word_data_id) => ({ word_data_id })),
			dateRange: { startDate: desde.toISOString(), endDate: hasta.toISOString() },
		},
	};
}

export { IDIOMAS };
